import { Component, DestroyRef, EventEmitter, inject, Optional, Output, signal } from '@angular/core';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { EntryService } from '../../entry.service';
import { GoogleAnalyticsService } from 'src/app/google-analytics.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { EntrySelectors } from 'src/app/state/entry.selectors';
import { Store } from '@ngrx/store';
import { EntryData, PlddtData, TedDomain } from '../../structure-data.models';
import { combineLatest, filter, map, tap } from 'rxjs';
import { PlatformService } from 'src/app/platform.service';
import { ModelConfidenceComponent } from '../model-confidence/model-confidence.component';
import { MappedTedDomain } from '../../domains-tab/domains-tab.component';
import { DownloadOption } from '../../entry-dropdown/entry-dropdown.component';
import { UtilService } from 'src/app/util.service';
export enum ViewState {
  List = 'list',
  Detail = 'detail',
}
@Component({
  selector: 'app-domains',
  templateUrl: './domains.component.html',
  styleUrl: './domains.component.css',
  standalone: false
})

export class DomainsComponent {
  @Output() handleSelectedDomainParent: EventEmitter<Object> = new EventEmitter();

  public expanded = signal<boolean>(false);
  private entryService = inject(EntryService);
  public accession = toSignal(this.store.select(EntrySelectors.accession));
  public entryData = signal<EntryData>({} as EntryData);
  public tedDomainData = signal<TedDomain[]>([]);
  public csvAmData = signal<string>('');
  private readonly destroyRef = inject(DestroyRef);
  public averageplddtDisplay:string;

  public activeDomain = signal<MappedTedDomain>(null);
  public mappedTedDomainData = signal<MappedTedDomain[]>([]);
  public tedDomainNumberForClickActive = signal<number | null>(null);

  public currentViewState = signal<ViewState>(ViewState.List);
  public viewStates = ViewState;
  public hydrationReady: boolean = false;
  public title = this.entryService.domainTitle;
  constructor(
    @Optional()
    private store: Store,
    private utilService: UtilService,
    private platform: PlatformService,
    public bottomSheetRef: MatBottomSheetRef<ModelConfidenceComponent>,
    public gaService: GoogleAnalyticsService,
  ) {}
  public plddtData: PlddtData;
  public selectedTabName = this.entryService.selectedTabName;
  private selectedComponent = this.entryService.selectedComponent;
  public uniqueTedDomains = signal<any[]>([])

  ngOnInit(): void {
    this.entryService.updateSelectedDomainTitle('Domains');
    if (this.platform.isBrowserPlatform()) {
      this.hydrationReady = true;
    }

    combineLatest([
      this.store.select(EntrySelectors.entryData).pipe(filter(Boolean)),
      this.store.select(EntrySelectors.tedDomainData).pipe(filter(Boolean)),
      this.store.select(EntrySelectors.csvAmData).pipe()
    ])
      .pipe(
        map(([entryData, tedDomainData, csvAmData]) => {
          this.entryData.set(entryData)
          this.tedDomainData.set(tedDomainData)
          this.csvAmData.set(csvAmData)

          if(this.tedDomainData()) {
            const tedDomainData1 = [];
            const seqLength = this.entryData().sequenceEnd;
            this.tedDomainData().forEach((item, index) => {
              for (let seg of item.segments) {
                tedDomainData1.push({
                  start: seg.af_start,
                  end: seg.af_end,
                  label: item.cath_label,
                  qscore: item.qscore,
                  cath_assignment_level: item.cath_assignment_level,
                  tedNumber: item.ted_domain_no,
                  plddt: item.plddt,
                  quality: this.getQuality(item.qscore, item.cath_assignment_level),
                  color: this.utilService.fetchColorByIndex(index),
                  left: (seg.af_start-1) / seqLength* 100 ,
                  right: (seqLength-seg.af_end) / seqLength* 100 ,
                });
              }
            });
            const sortedTedDomainData = tedDomainData1.sort((a, b) => {
              if (a.start === b.start) {
                return a.end - b.end;
              }
              return a.start - b.start;
            });
            const mappedTedDomainDataWithGap = sortedTedDomainData.map(
              (item, index) => {
                return {
                  ...item,
                  gap:
                    index !== sortedTedDomainData.length - 1
                      ? ((sortedTedDomainData[index + 1].start -
                          sortedTedDomainData[index].end) /
                          seqLength) *
                        262
                      : 0,
                };
              }
            );
            
            this.mappedTedDomainData.update(() => mappedTedDomainDataWithGap);
            this.activeDomain.set(this.mappedTedDomainData()[0]);
            const similarTeds = this.mappedTedDomainData().filter(
              (item) => item.tedNumber === this.activeDomain().tedNumber
            );
            const boundaries = this.getBoundaries(similarTeds);
            const length = this.getTotalLength(similarTeds);
            this.activeDomain.set({ ...this.mappedTedDomainData()[0], boundaries, length });
            this.tedDomainNumberForClickActive.set(this.activeDomain().tedNumber);
            this.entryService.setActiveDomain(this.activeDomain())
      
            this.entryService.activeDomain.subscribe((data:any)=>{
              const activeDomain = this.uniqueTedDomains().find(domain=>domain.tedNumber === data.tedNumber);
              // this.selectedRowCard.set(activeDomain);
             })
      
             this.uniqueTedDomains.set([]);
      
             const seenTedNumbers = new Set<number>();
             for (const item of this.mappedTedDomainData()) {
               if (!seenTedNumbers.has(item.tedNumber)) {
                 this.uniqueTedDomains().push(item);
                 seenTedNumbers.add(item.tedNumber);
               }
             }
          }
          
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  public navigateToDetail(domain) {
    this.handleSelectedDomainParent.emit(domain);
    const name = "Domain " + domain.tedNumber
    this.currentViewState.set(ViewState.Detail);

    const similarTeds = this.mappedTedDomainData().filter(
      (item) => item.tedNumber === domain.tedNumber
    );
    const boundaries = this.getBoundaries(similarTeds);
    const length = this.getTotalLength(similarTeds);
    const quality = this.getQuality(domain.qscore, domain.cath_assignment_level);
    const qscore = domain.qscore;
    const cath_assignment_level = domain.cath_assignment_level;
    const data = [
      ...similarTeds.map((domain) => ({
        struct_asym_id: 'A',
        start_residue_number: domain.start,
        end_residue_number: domain.end,
        focus: true,
        color: domain.color,
      })),
    ];
    // await this.pdbeMolstar.visual.focus(data);

    this.activeDomain.set({ ...domain, boundaries, length, qscore, cath_assignment_level, quality });
    this.entryService.updateSelectedDomainTitle(name);
    this.tedDomainNumberForClickActive.set(this.activeDomain().tedNumber);
    this.gaService.eventEmitter('mobile_domain_select', 'mobile_domains_interaction', 'tap', 'mobile_domain_selected', 'Select a domain from list of domains');
  }
  public async goBackToList() {
    this.currentViewState.set(ViewState.List);
    this.entryService.updateSelectedDomainTitle('Domains');
    // await this.molstarVisualisation.resetMobileMolstarInitial();
  }

  public getBoudariesSingle(tednumber){
    const similarTeds = this.mappedTedDomainData().filter(
      (item) => item.tedNumber === tednumber
    );
    const mappedSimilarTeds = similarTeds.map(
      (item) => item.start + '-' + item.end
    );
    return mappedSimilarTeds.join(', ');
  }
  public getQuality(qscore: number, assignment: string): string {
    if (qscore >= 75 && assignment.includes("C.A.T.H. Homologous Superfamily")) {
        return "High-confidence"; //2 stars
    } 
    if (qscore >= 70 && (assignment.includes("C.A.T.H. Homologous") || assignment.includes("C.A.T. Fold"))) {
        return "Moderate"; //1 star
    }
    return "Uncertain"; // No star/Empty star
  }

  private getBoundaries(similarTeds): string {
    const mappedSimilarTeds = similarTeds.map(
      (item) => item.start + '-' + item.end
    );
    return mappedSimilarTeds.join(', ');
  }

  private getTotalLength(similarTeds) {
    return similarTeds.reduce(
      (acc, current) => acc + (current.end - current.start) + 1,
      0
    );
  }

  formatPercentage(value: number | undefined | null): string {
    if (value == null) return '0.0';
    return (value * 100).toFixed(1);
  }
  public getPlddtLevel(plddt: number): string {
    return plddt > 90
      ? 'Very High'
      : plddt > 70
      ? 'High'
      : plddt > 50
      ? 'Low'
      : 'Very Low';
  }
  public closeBottomSheet(): void {
    this.bottomSheetRef.dismiss();
    this.entryService.updateSelectedComponent(null);
    this.entryService.updateSelectedTabName('');
  }

  public toggleBottomsheetHeight(): void {
    this.expanded.update((olamide) => !olamide);
    const container = document.querySelector(
      '.custom-bottom-sheet'
    ) as HTMLElement;
    if (container) {
      container.style.height = this.expanded() ? '50%' : '10%';
    }
    //for GA tag
    let tag = "bottomsheet_toggle_up";
    let gtagmsg = "Opening bottomsheet up";
    if(!this.expanded()){
      tag = "bottomsheet_toggle_down";
      gtagmsg = "Collapsing bottomsheet down";
    }
    this.gaService.eventEmitter('bottomsheet_toggle', 'mobile_overlay_interaction', 'tap', tag, gtagmsg);
  }
  ngOnDestroy(): void {
    // Clear the signal's value on destroy
    this.mappedTedDomainData.set([]);
  }
}
